// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { EventEmitter } from 'events';
import { DataCache } from '@shared/commons/DataCache';
import { Resources } from '@shared/localizations/Resources';
import { validateWatchlistName } from './WatchlistNameValidator';
import { WatchlistSheetItem } from './WatchlistSheetItem';
import { WatchlistSheets } from './WatchlistSheets';
import { InstrumentDescriptionUtils } from '../../Instruments/InstrumentDescriptionUtils';
import { RulesSet } from '../../Rules/RulesSet';
export var WatchlistSymbolStateEnum;
(function (WatchlistSymbolStateEnum) {
    WatchlistSymbolStateEnum[WatchlistSymbolStateEnum["None"] = 0] = "None";
    WatchlistSymbolStateEnum[WatchlistSymbolStateEnum["AddedToWatchlist"] = 1] = "AddedToWatchlist";
    WatchlistSymbolStateEnum[WatchlistSymbolStateEnum["RemoveFromWatchlist"] = 2] = "RemoveFromWatchlist";
    WatchlistSymbolStateEnum[WatchlistSymbolStateEnum["MaximumNumberReached"] = 3] = "MaximumNumberReached";
})(WatchlistSymbolStateEnum || (WatchlistSymbolStateEnum = {}));
export var WatchlistManagerEvents;
(function (WatchlistManagerEvents) {
    WatchlistManagerEvents["symbolAdded"] = "symbolAdded";
    WatchlistManagerEvents["symbolRemoved"] = "symbolRemoved";
    WatchlistManagerEvents["sheetUpdated"] = "sheetUpdated";
})(WatchlistManagerEvents || (WatchlistManagerEvents = {}));
export class _WatchlistManager {
    constructor() {
        this._defaultNewListName = Resources.getResource('screen.renameScreen.newList.placeholder');
        this._defaultListName = Resources.getResource('editableComboBox.defaultList');
        this._watchlistEventEmitter = new EventEmitter();
        this.items = [];
        this.sortItems = (a, b) => {
            const ax = [];
            const bx = [];
            const regex = /(\d+)|(\D+)/g;
            let match;
            while ((match = regex.exec(a.name)) !== null) {
                const [_, num, str] = match;
                ax.push([num !== undefined ? parseInt(num, 10) : Infinity, str || '']);
            }
            while ((match = regex.exec(b.name)) !== null) {
                const [_, num, str] = match;
                bx.push([num !== undefined ? parseInt(num, 10) : Infinity, str || '']);
            }
            while (ax.length > 0 && bx.length > 0) {
                const an = ax.shift();
                const bn = bx.shift();
                const nn = an[0] - bn[0] || an[1].localeCompare(bn[1]);
                if (nn)
                    return nn;
            }
            return ax.length - bx.length;
        };
    }
    updateStorageItems() {
        this.items = [];
        const items = this.getStorageItems();
        if (items.length === 0) {
            const defaultItem = this.getDefaultItem();
            this.items.push(defaultItem);
            this.saveStorageItems(this.items);
        }
        else {
            this.items = items;
        }
    }
    getFirstItem() {
        return this.items[0];
    }
    getItems() {
        return this.items;
    }
    addItem(newListName) {
        const error = this.validateItemName(newListName);
        if (isValidString(error)) {
            return error;
        }
        const menuItem = new WatchlistSheetItem(newListName);
        this.items.push(menuItem);
        this.saveStorageItems(this.items);
        return null;
    }
    renameItem(oldListName, newListName) {
        const error = this.validateItemName(newListName);
        if (isValidString(error)) {
            return error;
        }
        const menuItem = this.getItemByName(oldListName);
        this.removeItemByName(oldListName);
        menuItem.name = newListName;
        this.items.push(menuItem);
        this.saveStorageItems(this.items);
        return null;
    }
    removeItem(listName) {
        const error = this.validateItemRemove();
        if (isValidString(error)) {
            return error;
        }
        this.removeItemByName(listName);
        this.saveStorageItems(this.items);
        return null;
    }
    addSymbolIDToItemByName(itemName, instDescr) {
        const symbolID = InstrumentDescriptionUtils.createInteriorId(instDescr);
        const item = this.getItemByName(itemName);
        if (!this.canAddSymbol()) {
            return false;
        }
        if (item.has(symbolID)) {
            return true;
        }
        item.add(instDescr);
        this.emitSymbolAdded(itemName, symbolID);
        this.saveStorageByItem(item);
        return true;
    }
    removeSymbolIDFromItemByName(itemName, instDescr) {
        const item = this.getItemByName(itemName);
        const symbolID = InstrumentDescriptionUtils.createInteriorId(instDescr);
        if (!item.has(symbolID)) {
            return;
        }
        item.delete(instDescr);
        this.emitSymbolRemoved(itemName, symbolID);
        this.saveStorageByItem(item);
    }
    setNewSymbolsForItem(itemName, instrDescriptions) {
        const item = this.getItemByName(itemName);
        item.set(instrDescriptions);
        this.emitItemUpdated(itemName);
        this.saveStorageByItem(item);
    }
    checkExistSymbolIDInAnyItems(symbolID) {
        const items = this.getItems();
        for (const item of items) {
            if (item.has(symbolID)) {
                return true;
            }
        }
        return false;
    }
    checkExistSymbolIDInItemName(name, symbolID) {
        const item = this.getItemByName(name);
        return item.has(symbolID);
    }
    // suffix BeforeAction - add/remove actions.
    getSymbolIDStateInItemBeforeAction(itemName, symbolID) {
        const symbolInWL = this.checkExistSymbolIDInItemName(itemName, symbolID);
        if (symbolInWL) {
            return WatchlistSymbolStateEnum.RemoveFromWatchlist;
        }
        else {
            if (this.canAddSymbol()) {
                return WatchlistSymbolStateEnum.AddedToWatchlist;
            }
            else {
                return WatchlistSymbolStateEnum.MaximumNumberReached;
            }
        }
    }
    canAddSymbol() {
        const count = DataCache.getRuleNumberValueForMyUser(RulesSet.VALUE_THROTLING_PANEL_LIMIT_WATCHLIST_ROWS);
        if (count === -1) {
            return true;
        }
        const items = this.getItems();
        const symbolIds = WatchlistSheets.getAllInstrumentDescriptions(items);
        if (count <= symbolIds.length) {
            return false;
        }
        return true;
    }
    validateItemName(name) {
        const existingNames = this.items.map((item) => item.name.trim());
        return validateWatchlistName(name, existingNames);
    }
    validateItemRemove() {
        if (this.items.length === 1) {
            return Resources.getResource('screen.remove.lastlist');
        }
        return null;
    }
    getItemByName(name) {
        const findItem = this.items.find((item) => item.name === name);
        return findItem !== null && findItem !== void 0 ? findItem : null;
    }
    removeItemByName(name) {
        const index = this.items.findIndex((item) => item.name === name);
        if (index !== -1) {
            this.items.splice(index, 1);
            return true;
        }
        return false;
    }
    saveStorageByItem(updateItem) {
        const updateIndex = this.items.findIndex((item) => item.name === updateItem.name);
        if (updateIndex !== -1) {
            this.items[updateIndex] = updateItem;
        }
        this.saveStorageItems(this.items);
    }
    saveStorageItems(items) {
        items.sort(this.sortItems);
        WatchlistSheets.items = items;
    }
    getPreferredItemName() {
        let listName = this._defaultNewListName;
        let ind = 0;
        let newNameFound = false;
        const len = this.items.length;
        do {
            ind++;
            newNameFound = true;
            for (let i = 0; i < len; i++) {
                if (this.items[i].name === listName) {
                    listName = this._defaultNewListName + ' ' + ind;
                    newNameFound = false;
                }
            }
        } while (!newNameFound);
        return listName;
    }
    subscribeEvent(eventType, listener) {
        this._watchlistEventEmitter.addListener(eventType, listener);
    }
    unsubscribeEvent(eventType, listener) {
        this._watchlistEventEmitter.removeListener(eventType, listener);
    }
    getStorageItems() {
        return WatchlistSheets.items;
    }
    getDefaultItem() {
        const defaultItem = new WatchlistSheetItem(this._defaultListName);
        return defaultItem;
    }
    emitSymbolAdded(itemName, symbolID) {
        this._watchlistEventEmitter.emit(WatchlistManagerEvents.symbolAdded, itemName, symbolID);
        this.emitItemUpdated(itemName);
    }
    emitSymbolRemoved(itemName, symbolID) {
        this._watchlistEventEmitter.emit(WatchlistManagerEvents.symbolRemoved, itemName, symbolID);
        this.emitItemUpdated(itemName);
    }
    emitItemUpdated(itemName) {
        this._watchlistEventEmitter.emit(WatchlistManagerEvents.sheetUpdated, itemName);
    }
}
export const WatchlistManager = new _WatchlistManager();
